import { useMemo, useState } from 'react';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { deleteJobTitle, fetchJobTitleList } from './api';
import JobTitleTableColumnConfig, {
    DefaultVisibleJobTitleColumns,
    SearchableJobTitleTableColumnConfig,
} from './job-title-column-config';
import { Box, Button, Dialog, Stack } from '@mui/material';
import { dispatch, useSelector } from '../../store';
import { openJobTitleForm, setJobTitle } from '../../store/reducers/job-title';
import { PopupTransition } from '../../components/@extended/Transitions';
import JobTitleForm from './job-title-form';
import DeleteButton from 'pages/calendar/delete-button';
import { Row } from 'react-table';
import JobTitleDto from 'common/dto-types/contacts/job-title-dto';
import IconButton from 'components/@extended/IconButton';
import { EditOutlined } from '@ant-design/icons';

const JobTitleListPage = () => {
    const [jobTitleList, setJobTitleList] = useState<Array<JobTitleDto>>([]);
    const defaultVisibleColumnConfig = useMemo(() => DefaultVisibleJobTitleColumns, []);

    const defaultColumnConfig = useMemo(
        () => [
            ...JobTitleTableColumnConfig,
            {
                Header: 'Actions',
                Cell: (row: { row: Row }) => (
                    <>
                        <IconButton
                            shape="rounded"
                            onClick={() => {
                                dispatch(setJobTitle(row.row.original as JobTitleDto));
                                dispatch(openJobTitleForm());
                            }}
                        >
                            <EditOutlined />
                        </IconButton>
                        <DeleteButton
                            deleteCallback={() => {
                                const jobTitle = row.row.original as any;
                                return deleteJobTitle(jobTitle.id);
                            }}
                            refreshCallback={async () =>
                                await fetchData({
                                    pageSize: 1000,
                                    pageIndex: 0,
                                    filters: [],
                                    searchTerm: null,
                                })
                            }
                        />
                    </>
                ),
            },
        ],
        [],
    );

    const isFormOpen = useSelector((state) => state.jobTitle.isFormOpen);

    const fetchData: BaseTableFetchFuncProps = async ({ pageSize, pageIndex, filters, searchTerm }) => {
        const data = await fetchJobTitleList({
            pageIndex,
            pageSize,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableJobTitleTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setJobTitleList(data.elements);
        }
    };

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2 }}>
                    <Button variant="contained" color="primary" onClick={() => dispatch(openJobTitleForm())}>
                        Add JobTitle
                    </Button>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={jobTitleList}
                        fetchData={fetchData}
                        pageCount={0}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                    />
                </ScrollX>
            </Stack>
            {isFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <JobTitleForm
                        refreshCallback={() =>
                            fetchData({ pageSize: 1000, pageIndex: 0, filters: [], searchTerm: null })
                        }
                    />
                </Dialog>
            )}
        </MainCard>
    );
};

export default JobTitleListPage;
